import { Box, Button, HStack, Skeleton, Text } from '@chakra-ui/react';
import {
  CheckCircle,
  Clock,
  Hourglass,
  Prohibition,
  WarningTriangle,
  XmarkCircle,
} from 'iconoir-react';
import { type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { BREATHE_ANIMATION } from './boost-animation';
import type { BoostFooterIconKey, BoostFooterIntent } from './boost-footer-intent';

const FOOTER_FADE = 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.82))';

// A short entrance (keyframes in index.css) for the inline boost state block when
// it swaps into the right pane, keyed on the resolved status so it replays on each
// transition. Disabled under prefers-reduced-motion via the media guard in index.css.
export const BOOST_STATE_ENTER_ANIMATION = 'boost-state-enter 180ms ease-out';

const ICONS: Record<BoostFooterIconKey, ComponentType<{ width?: number; height?: number }>> = {
  hourglass: Hourglass,
  check: CheckCircle,
  clock: Clock,
  declined: XmarkCircle,
  blocked: Prohibition,
  warning: WarningTriangle,
};

interface BoostFooterProps {
  intent: BoostFooterIntent;
}

/**
 * Sticky footer for the boost surface. Renders one of three shapes so the
 * brand can read its turn at a glance: a filled CTA when it can act, a quiet
 * status row when it is waiting, and nothing when there is no surface. Every
 * shape is the same height so the footer never reflows as the state changes.
 */
export function BoostFooter({ intent }: BoostFooterProps) {
  const { t } = useTranslation();

  if (intent.mode === 'hidden') {
    return null;
  }

  return (
    <Box
      position="absolute"
      left={0}
      right={0}
      bottom={0}
      zIndex={2}
      px={{ base: 4, md: 6 }}
      pt={4}
      pb={4}
      bg="rgba(255,255,255,0.82)"
      backdropFilter="blur(8px)"
      _before={{
        content: '""',
        position: 'absolute',
        left: 0,
        right: 0,
        top: '-44px',
        h: '44px',
        bg: FOOTER_FADE,
        backdropFilter: 'blur(3px)',
        pointerEvents: 'none',
      }}
      data-testid="mention-boost-sticky-footer"
    >
      {intent.mode === 'skeleton' ? (
        <Skeleton h="40px" rounded="md" />
      ) : intent.mode === 'action' ? (
        <Button
          position="relative"
          zIndex={1}
          width="full"
          colorPalette={intent.destructive ? 'red' : 'blue'}
          variant={intent.destructive ? 'outline' : 'solid'}
          fontWeight="semibold"
          onClick={intent.onClick}
          data-testid={intent.destructive ? 'boost-stop' : undefined}
        >
          {t(intent.labelKey)}
        </Button>
      ) : (
        <BoostFooterStatusRow intent={intent} />
      )}
    </Box>
  );
}

function BoostFooterStatusRow({
  intent,
}: {
  intent: Extract<BoostFooterIntent, { mode: 'status' }>;
}) {
  const { t } = useTranslation();
  const Icon = ICONS[intent.icon];

  return (
    <HStack
      position="relative"
      zIndex={1}
      h="44px"
      px={2.5}
      gap={2.5}
      rounded="lg"
      border="1px solid"
      borderColor={`${intent.tone}.200`}
      bg={`${intent.tone}.50`}
    >
      <Box
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxSize="28px"
        rounded="md"
        bg={`${intent.tone}.100`}
        color={`${intent.tone}.700`}
        animation={intent.pulse ? BREATHE_ANIMATION : undefined}
      >
        <Icon width={15} height={15} />
      </Box>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color={`${intent.tone}.800`}
        letterSpacing="-0.01em"
        lineClamp={1}
      >
        {t(intent.labelKey)}
      </Text>
    </HStack>
  );
}
